import { FC, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';

import Box from '@o2/ui/Box';
import Divider from '@o2/ui/Divider';
import Grid from '@o2/ui/Grid';
import IconButton from '@o2/ui/IconButton';
import Link from '@o2/ui/Link';
import Stack from '@o2/ui/Stack';
import Typography from '@o2/ui/Typography';

import CrossIcon from '@o2/icons/Cross';

import type { Wireframe, WireframeModalProps } from './wfTypes';

const WireframeInfoModal = styled(Box, {
  name: 'WireframeInfoModal',
  slot: 'root',
})(({ theme }) => ({
  position: 'fixed',
  right: 0,
  bottom: '50px',
  width: '280px',
  minHeight: '130px',
  backgroundColor: theme.palette.background.internal,
  opacity: 0.7,
  border: `2px solid ${theme.palette.internal.main}`,
  borderRight: 0,
  borderRadius: '20px 0 0 20px',
}));

const MatchedWfRow: FC<{ id?: Wireframe['id']; name: string }> = ({ id, name }) => {
  return (
    <Stack direction="column">
      <Typography>
        {typeof id !== 'undefined' && `${id} –`} {name}
      </Typography>
    </Stack>
  );
};

const WireframeModal: FC<WireframeModalProps> = ({
  wireframes,
  isInternal = false,
  axureUrl,
  figmaUrl,
  projectName,
  doesWireframeMatchCurrentPage,
  ...other
}) => {
  const matchedWfs = useMemo(() => {
    const wfs = wireframes
      .filter(doesWireframeMatchCurrentPage)
      .map((wf) => <MatchedWfRow id={wf.id} name={wf.name} key={wf.id} />);

    // if none matched
    if (!wfs.length) {
      wfs.push(<MatchedWfRow name="?" key="?" />);
    }

    return wfs;
  }, [doesWireframeMatchCurrentPage, wireframes]);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const toggleWFModal = (e: KeyboardEvent) => {
    if (e.isTrusted && e.ctrlKey && e.altKey && e.code === 'KeyW') {
      setModalOpen(!modalOpen);
    }
  };

  useEffect(() => {
    if (isInternal) {
      document.addEventListener('keydown', toggleWFModal);
    }

    return () => {
      if (isInternal) {
        document.removeEventListener('keydown', toggleWFModal);
      }
    };
  });

  if (!isInternal || !modalOpen) return null;

  return (
    <WireframeInfoModal {...other}>
      <Box sx={{ padding: 1, paddingLeft: 2 }}>
        <Stack direction="column" spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography color="internal" variant="h6">
                {projectName}
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={() => setModalOpen(false)}>
                <CrossIcon />
              </IconButton>
            </Box>
          </Stack>
          <Box>
            <Grid container spacing={1}>
              <Grid item xs={2}>
                <b>WF</b>
              </Grid>
              <Grid item xs={10}>
                {matchedWfs}
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Stack direction="row" justifyContent="center" spacing={2}>
            {axureUrl && (
              <Link underline="hover" href={axureUrl} target="_blank">
                Wireframes
              </Link>
            )}
            {figmaUrl && (
              <Link underline="hover" href={figmaUrl} target="_blank">
                Figma
              </Link>
            )}
          </Stack>
        </Stack>
      </Box>
    </WireframeInfoModal>
  );
};

WireframeModal.displayName = 'WireframeModal';

export default WireframeModal;
